/**
 * WebSocket Context for Real-time Updates
 * 
 * Provides real-time updates throughout the application:
 * - Device status changes
 * - New events/alarms
 * - Dashboard statistics
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { triggerAlarm, showBrowserNotification, initAlarmSystem, registerPushSubscription, canUsePush } from '@/lib/alarm';

const WebSocketContext = createContext(null);

const getWebSocketUrl = () => {
  return process.env.REACT_APP_BACKEND_URL || '';
};

export function WebSocketProvider({ children }) {
  const { user, token } = useAuth();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const listenersRef = useRef(new Map());

  // Initialize alarm system on mount
  useEffect(() => { initAlarmSystem(); }, []);

  // Check push support and existing subscription
  useEffect(() => {
    const check = async () => {
      if (!canUsePush()) return;
      setPushSupported(true);
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) setPushEnabled(true);
      } catch {}
    };
    check();
  }, []);

  // Function to enable push — must be called from user gesture
  const enablePush = useCallback(async () => {
    if (!token) return false;
    try {
      const sub = await registerPushSubscription(token);
      if (sub) {
        setPushEnabled(true);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [token]);

  // Connect to WebSocket
  useEffect(() => {
    if (!user || !token) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    const wsUrl = getWebSocketUrl();
    
    const newSocket = io(wsUrl, {
      path: '/api/socket.io/',
      auth: {
        tenant_id: user.tenant_id,
        user_id: user.id,
        token: token
      },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('WebSocket connected');
      setConnected(true);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      setConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.warn('WebSocket connection error:', error.message);
    });

    // Application events
    newSocket.on('connected', (data) => {
      console.log('WebSocket welcome:', data);
    });

    newSocket.on('device_status', (data) => {
      setLastUpdate({ type: 'device_status', data, timestamp: new Date() });
      // Notify listeners
      listenersRef.current.get('device_status')?.forEach(cb => cb(data));
    });

    newSocket.on('new_event', (data) => {
      setLastUpdate({ type: 'new_event', data, timestamp: new Date() });
      const event = data.event;

      // Toast notification
      if (event && event.event_type === 'vape') {
        toast.error(`VAPE LARM: ${event.device_name || 'Sensor'} — ${event.room_name || 'Rum'}`, {
          duration: 15000,
        });
      } else if (event && event.event_type === 'suspect') {
        toast.warning(`Misstänkt aktivitet: ${event.device_name || 'Sensor'} — ${event.room_name || 'Rum'}`, {
          duration: 8000,
        });
      }

      // ALARM: Sound + Vibration
      triggerAlarm(event);

      // Browser push notification
      showBrowserNotification(event);

      // Track unread count
      setUnreadCount(prev => prev + 1);
      // Notify listeners
      listenersRef.current.get('new_event')?.forEach(cb => cb(data));
    });

    newSocket.on('dashboard_update', (data) => {
      setLastUpdate({ type: 'dashboard_update', data, timestamp: new Date() });
      // Notify listeners
      listenersRef.current.get('dashboard_update')?.forEach(cb => cb(data));
    });

    newSocket.on('pong', (data) => {
      console.log('WebSocket pong:', data);
    });

    // Sensor reading for test lab live view
    newSocket.on('sensor_reading', (data) => {
      listenersRef.current.get('sensor_reading')?.forEach(cb => cb(data));
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [user, token]);

  // Subscribe to events
  const subscribe = useCallback((eventType, callback) => {
    if (!listenersRef.current.has(eventType)) {
      listenersRef.current.set(eventType, new Set());
    }
    listenersRef.current.get(eventType).add(callback);

    // Return unsubscribe function
    return () => {
      listenersRef.current.get(eventType)?.delete(callback);
    };
  }, []);

  // Subscribe to specific device
  const subscribeToDevice = useCallback((deviceId) => {
    if (socket && connected) {
      socket.emit('subscribe_device', { device_id: deviceId });
    }
  }, [socket, connected]);

  // Unsubscribe from device
  const unsubscribeFromDevice = useCallback((deviceId) => {
    if (socket && connected) {
      socket.emit('unsubscribe_device', { device_id: deviceId });
    }
  }, [socket, connected]);

  // Ping to keep connection alive
  const ping = useCallback(() => {
    if (socket && connected) {
      socket.emit('ping', { timestamp: new Date().toISOString() });
    }
  }, [socket, connected]);

  const clearUnread = useCallback(() => setUnreadCount(0), []);

  const value = {
    socket,
    connected,
    lastUpdate,
    unreadCount,
    clearUnread,
    subscribe,
    subscribeToDevice,
    unsubscribeFromDevice,
    ping,
    pushEnabled,
    pushSupported,
    enablePush,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

// Hook for subscribing to specific event types
export function useRealtimeEvent(eventType, callback) {
  const { subscribe } = useWebSocket();

  useEffect(() => {
    const unsubscribe = subscribe(eventType, callback);
    return unsubscribe;
  }, [eventType, callback, subscribe]);
}
